// src/features/wf2/components/W2Page.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useDeferredValue,
  memo,
} from "react";
import "./W2Page.css";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/pt-br";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { listW2, moveDateW2, advanceStageW2, W2_LOCAL_EVENT } from "../api";
import W2Card from "./W2Card";
import W2FichaModal from "../popup/W2FichaModal";
import W2NovaProgramar from "../popup/W2NovaProgramar.module.jsx";
import W2MoverDataModal from "../popup/W2MoverDataModal";
import W2InfoPopup from "../popup/W2InfoPopup";
import W2DateErrorModal from "../popup/W2DateErrorModal";
import "../popup/W2InfoPopup.css";

// realtime
import {
  connectRealtime,
  on as rtOn,
  off as rtOff,
  RT,
  joinRoom,
  leaveRoom,
  RT_META,
  onRealtimeMeta,
  offRealtimeMeta,
} from "../../../api/realtime";
import { makeWorkflowAllRoom, makeWorkflowDayRoom } from "../../../api/realtime.rooms";

/* =================== Dayjs TZ =================== */
const TZ = "America/Sao_Paulo";
const W2_PRE_META_HORA_RAW = String(process.env.REACT_APP_W2_PRE_META_HORA || "20:00:00").trim();
const W2_PRE_META_HORA = /^\d{2}:\d{2}:\d{2}$/.test(W2_PRE_META_HORA_RAW)
  ? W2_PRE_META_HORA_RAW
  : /^\d{2}:\d{2}$/.test(W2_PRE_META_HORA_RAW)
  ? `${W2_PRE_META_HORA_RAW}:00`
  : "20:00:00";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("pt-br");
dayjs.tz.setDefault(TZ);

/* =================== Constantes =================== */
const DIAS_PT = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const STATUS_LABEL = {
  em_andamento: "Não Iniciado",
  primeira_previa: "Primeira Prévia",
  segunda_previa: "Segunda Prévia",
  executado: "Executado",
  reprogramar: "Reprogramar",
  finalizado: "Finalizado",
};
const STATUS_OPCOES = Object.keys(STATUS_LABEL);

const STATUS_LABEL_W2 = {
  nao_iniciado: "NÃ£o Iniciado",
  pre_programacao: "PrÃ©-programaÃ§Ã£o",
  programado: "Programado",
  editado: "Editado",
  reprogramado: "Reprogramado",
  negado: "Negado",
  aprovado: "Aprovado",
  cancelado: "Cancelado",
};
const STATUS_OPCOES_W2 = Object.keys(STATUS_LABEL_W2);

const STATUS_LABEL_W2_UI = {
  nao_iniciado: "N\u00E3o Iniciado",
  pre_programacao: "Pr\u00E9-programa\u00E7\u00E3o",
  programado: "Programado",
  editado: "Editado",
  reprogramado: "Reprogramado",
  negado: "Negado",
};
const STATUS_OPCOES_W2_UI = Object.keys(STATUS_LABEL_W2_UI);
const STATUS_FILTER_OPTIONS_W2 = STATUS_OPCOES_W2_UI.map((k) => ({
  value: k,
  label: STATUS_LABEL_W2_UI[k],
}));
void STATUS_LABEL_W2;
void STATUS_OPCOES_W2;

/* =================== Helpers de data =================== */

// Dayjs inválido "do jeito certo" (Dayjs NÃO tem dayjs.invalid())
function invalidDayjs() {
  return dayjs(new Date(NaN));
}

function isYMD(s) {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.trim());
}

function toKeyYYYYMMDD(input) {
  if (input == null) return null;

  // epoch number
  if (typeof input === "number" && Number.isFinite(input)) {
    const d = dayjs(input).tz(TZ);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  }

  // Date object
  if (input instanceof Date) {
    const d = dayjs(input).tz(TZ);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  }

  if (typeof input === "string") {
    let s = input.trim();
    if (!s) return null;

    // epoch string
    if (/^\d{10}$/.test(s)) {
      const d = dayjs(Number(s) * 1000).tz(TZ);
      return d.isValid() ? d.format("YYYY-MM-DD") : null;
    }
    if (/^\d{13}$/.test(s)) {
      const d = dayjs(Number(s)).tz(TZ);
      return d.isValid() ? d.format("YYYY-MM-DD") : null;
    }

    // "YYYY-MM-DD"
    if (isYMD(s)) return s;

    // "DD/MM/YYYY"
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const d = dayjs.tz(s, "DD/MM/YYYY", TZ);
      return d.isValid() ? d.format("YYYY-MM-DD") : null;
    }

    // "YYYY-MM-DD HH:mm(:ss)"
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(s)) {
      const fmt =
        s.includes(":") && s.split(":").length === 3
          ? "YYYY-MM-DD HH:mm:ss"
          : "YYYY-MM-DD HH:mm";
      const d = dayjs.tz(s, fmt, TZ);
      return d.isValid() ? d.format("YYYY-MM-DD") : null;
    }

    // ISO / offset / Z -> parse como instante e converte pra TZ
    if (s.includes("T") || /[Zz]$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(s)) {
      const d = dayjs(s);
      if (d.isValid()) return d.tz(TZ).format("YYYY-MM-DD");

      // fallback: interpretar como "wall clock" em TZ
      const d2 = dayjs.tz(s.replace(" ", "T"), TZ);
      return d2.isValid() ? d2.format("YYYY-MM-DD") : null;
    }

    // fallback geral: tenta interpretar como TZ
    const d = dayjs.tz(s, TZ);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  }

  // fallback geral
  try {
    const d = dayjs(input).tz(TZ);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  } catch {
    return null;
  }
}

function toLocalDay(input) {
  const key = toKeyYYYYMMDD(input);
  if (!key) return invalidDayjs();
  // cria a data no TZ, no começo do dia
  const d = dayjs.tz(key, "YYYY-MM-DD", TZ).startOf("day");
  return d.isValid() ? d : invalidDayjs();
}

function getDiasCalendario(inicio, final) {
  const dias = [];
  let dia = inicio;
  while (dia.isBefore(final) || dia.isSame(final, "day")) {
    dias.push(dia);
    dia = dia.add(1, "day");
  }
  return dias;
}

function getWeekRangeSundayToSaturday(baseDay = dayjs.tz().startOf("day")) {
  const anchor = baseDay.startOf("day");
  const start = anchor.subtract(anchor.day(), "day");
  const end = start.add(6, "day");
  return { start, end };
}

function firstClockToMinutes(val) {
  if (typeof val !== "string") return null;
  const m = val.match(/(\d{1,2}):([0-5]\d)/);
  if (!m) return null;
  const h = parseInt(m[1], 10) || 0;
  const min = parseInt(m[2], 10) || 0;
  return h * 60 + min;
}

function toMinutes(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "number" && Number.isFinite(val)) return val;

  if (val instanceof Date && !isNaN(val.getTime())) {
    return val.getHours() * 60 + val.getMinutes();
  }

  if (typeof val === "string") {
    const first = firstClockToMinutes(val);
    if (first !== null) return first;

    // se parece data/hora
    if (/T|\d{4}-\d{2}-\d{2}/.test(val)) {
      const s = val.includes(" ") ? val.replace(" ", "T") : val;
      const d = dayjs(s);
      if (d.isValid()) return d.tz(TZ).hour() * 60 + d.tz(TZ).minute();

      const d2 = dayjs.tz(s, TZ);
      if (d2.isValid()) return d2.hour() * 60 + d2.minute();
    }
  }

  try {
    const d = dayjs(val);
    if (d.isValid()) return d.tz(TZ).hour() * 60 + d.tz(TZ).minute();
  } catch {}

  return null;
}

function extrairMinutosDoProg(p) {
  const startCandidates = [
    p.data_hora_inicio,
    p.inicio_trabalho,
    p.horario_inicio,
    p.hora_inicio,
    p.primeiro_inicio,
    p.segundo_inicio,
    p.terceiro_inicio,
    p.inicio,
    p.start,
    p.hora_programada_inicio,
    p.horario,
    p.hora,
    // W2 (muito comum)
    p.horario_do_intervalo,
  ];
  for (const c of startCandidates) {
    const mins = toMinutes(c);
    if (mins !== null) return mins;
  }
  return 24 * 60;
}

/* =================== Helpers UI =================== */

function normalizeSearchText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function normalizeFilterValue(value) {
  return String(value ?? "").trim();
}

function filterMatchKey(value) {
  return normalizeSearchText(normalizeFilterValue(value));
}

function uniqFilterOptions(values = []) {
  const seen = new Set();
  const out = [];
  for (const raw of values) {
    const label = normalizeFilterValue(raw);
    if (!label) continue;
    const key = filterMatchKey(label);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

const MultiSelect = memo(function MultiSelect({
  options = [],
  value,
  onChange,
  placeholder = "Todos",
  title = "Selecione",
  searchable = false,
  searchPlaceholder = "Buscar...",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const setSelected = (next) => onChange(Array.isArray(value) ? next : next[0] || "");
  const getVal = useCallback((opt) => (typeof opt === "string" ? opt : opt?.value), []);
  const getLabel = useCallback(
    (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? ""),
    []
  );
  const normalizedOptions = useMemo(
    () =>
      options
        .map((opt) => ({ value: getVal(opt), label: getLabel(opt) }))
        .filter((opt) => opt.value !== undefined && opt.value !== null && opt.value !== ""),
    [getLabel, getVal, options]
  );
  const selectedLabels = useMemo(() => {
    const labels = normalizedOptions
      .filter((opt) => selectedSet.has(opt.value))
      .map((opt) => opt.label)
      .filter(Boolean);
    if (labels.length) return labels;
    return selected.map((item) => String(item ?? "").trim()).filter(Boolean);
  }, [normalizedOptions, selected, selectedSet]);
  const label =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const normalizedQuery = useMemo(() => normalizeSearchText(query), [query]);
  const filteredOptions = useMemo(() => {
    if (!searchable || !normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((opt) => normalizeSearchText(opt.label).includes(normalizedQuery));
  }, [normalizedOptions, normalizedQuery, searchable]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    if (!searchable) return;
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open, searchable]);

  const handleItem = (opt) => {
    const val = getVal(opt);
    const s = new Set(selected);
    if (s.has(val)) s.delete(val);
    else s.add(val);
    setSelected(Array.from(s));
  };

  const handleClear = () => {
    setSelected([]);
    setQuery("");
  };

  return (
    <div className="w2-msRoot" ref={rootRef}>
      <button
        className={`w2-msBtn ${open ? "open" : ""}`}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="w2-msBtnText">
          <span className={`w2-msText ${selectedLabels.length ? "" : "is-placeholder"}`}>{label}</span>
          {selectedLabels.length > 1 ? (
            <span className="w2-msMeta">{selectedLabels.length} selecionados</span>
          ) : null}
        </span>
        <span className="w2-caret">▾</span>
      </button>

      {open && (
        <div className="w2-msPanel">
          <div className="w2-msHeader">
            <div className="w2-msHeaderCopy">
              <span className="w2-msHeaderTitle">{title}</span>
              <span className="w2-msHeaderMeta">
                {selectedLabels.length
                  ? `${selectedLabels.length} marcado${selectedLabels.length > 1 ? "s" : ""}`
                  : "Nenhum marcado"}
              </span>
            </div>
            <button type="button" className="w2-msActionBtn" onClick={handleClear}>
              Limpar
            </button>
          </div>
          {searchable && (
            <div className="w2-msSearchWrap">
              <input
                ref={searchRef}
                type="text"
                className="w2-msSearch"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          <div className="w2-msListWrap">
            <div className="w2-msList">
              {filteredOptions.map((opt) => {
                const val = getVal(opt);
                const lab = getLabel(opt);
                const checked = selectedSet.has(val);
                return (
                  <button
                    type="button"
                    key={val}
                    className={`w2-msItem ${checked ? "is-selected" : ""}`}
                    onClick={() => handleItem(opt)}
                    aria-pressed={checked}
                  >
                    <span className="w2-msLabel">{lab}</span>
                  </button>
                );
              })}
              {!filteredOptions.length && (
                <div className="w2-msEmpty">Nenhum resultado encontrado.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

/* =================== Status canônico =================== */
const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

const W2DateDropdown = memo(function W2DateDropdown({
  value,
  onChange,
  minDate = null,
  maxDate = null,
  ariaLabel = "Selecionar data",
  panelAlign = "left",
}) {
  const rootRef = useRef(null);
  const selectedKey = value?.isValid?.() ? value.format("YYYY-MM-DD") : "__today__";
  const selectedDate = useMemo(
    () => (value?.isValid?.() ? value.startOf("day") : dayjs.tz().startOf("day")),
    [selectedKey]
  );
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => selectedDate.startOf("month"));
  const minDay = minDate?.isValid?.() ? minDate.startOf("day") : null;
  const maxDay = maxDate?.isValid?.() ? maxDate.startOf("day") : null;

  useEffect(() => {
    if (open) return;
    const nextMonth = selectedDate.startOf("month");
    setViewMonth((prev) => (prev?.isSame?.(nextMonth, "month") ? prev : nextMonth));
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const monthStart = viewMonth.startOf("month");
  const gridStart = monthStart.subtract(monthStart.day(), "day");
  const calendarDays = useMemo(
    () => Array.from({ length: 42 }, (_, index) => gridStart.add(index, "day")),
    [gridStart]
  );
  const today = dayjs.tz().startOf("day");

  const monthPrevDisabled =
    !!minDay && viewMonth.subtract(1, "month").endOf("month").isBefore(minDay, "day");
  const monthNextDisabled =
    !!maxDay && viewMonth.add(1, "month").startOf("month").isAfter(maxDay, "day");

  const handlePick = useCallback(
    (day) => {
      if (!day?.isValid?.()) return;
      if (minDay && day.isBefore(minDay, "day")) return;
      if (maxDay && day.isAfter(maxDay, "day")) return;
      onChange(day.startOf("day"));
      setOpen(false);
    },
    [maxDay, minDay, onChange]
  );

  return (
    <div
      className={`w2-dateRoot w2-dateRoot--${panelAlign}${open ? " is-open" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`w2-dateBtn ${open ? "open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="w2-dateValue">{selectedDate.format("DD/MM/YYYY")}</span>
      </button>

      {open && (
        <div className="w2-datePanel" role="dialog" aria-label={ariaLabel}>
          <div className="w2-datePanelHead">
            <button
              type="button"
              className="w2-dateNav"
              onClick={() => !monthPrevDisabled && setViewMonth((prev) => prev.subtract(1, "month"))}
              disabled={monthPrevDisabled}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className="w2-datePanelTitle">{viewMonth.format("MMMM [de] YYYY")}</div>
            <button
              type="button"
              className="w2-dateNav"
              onClick={() => !monthNextDisabled && setViewMonth((prev) => prev.add(1, "month"))}
              disabled={monthNextDisabled}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className="w2-dateWeekdays">
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className="w2-dateWeekday">
                {weekday}
              </span>
            ))}
          </div>

          <div className="w2-dateGrid">
            {calendarDays.map((day) => {
              const isOutside = day.month() !== viewMonth.month();
              const isSelected = day.isSame(selectedDate, "day");
              const isToday = day.isSame(today, "day");
              const isDisabled =
                (minDay && day.isBefore(minDay, "day")) ||
                (maxDay && day.isAfter(maxDay, "day"));

              return (
                <button
                  type="button"
                  key={day.format("YYYY-MM-DD")}
                  className={[
                    "w2-dateDay",
                    isOutside ? "is-outside" : "",
                    isSelected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                    isDisabled ? "is-disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handlePick(day)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          <div className="w2-datePanelFoot">
            <button
              type="button"
              className="w2-dateAction"
              onClick={() => handlePick(today)}
              disabled={
                (minDay && today.isBefore(minDay, "day")) ||
                (maxDay && today.isAfter(maxDay, "day"))
              }
            >
              Hoje
            </button>
            <button
              type="button"
              className="w2-dateAction w2-dateActionGhost"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

function statusCanon(obj) {
  const raw =
    obj?.status_wf2 ??
    obj?.etapa_atual ??
    obj?.status ??
    obj?.status_programacaod1 ??
    obj?.fase ??
    obj?.etapa ??
    null;

  const s = raw ? String(raw).toLowerCase().trim() : "";
  return s || "em_andamento";
}

function statusCanonW2(obj) {
  const raw =
    obj?.status_wf2 ??
    obj?.etapa_atual ??
    obj?.status ??
    obj?.status_programacaod1 ??
    obj?.fase ??
    obj?.etapa ??
    null;

  const s = raw ? String(raw).trim().toUpperCase() : "";

  if (!s || s === "SALVO" || s === "EM_ANDAMENTO" || s === "PENDENTE") {
    return "nao_iniciado";
  }
  if (s === "EM_VALIDACAO_AUTOMATICA") return "pre_programacao";
  if (s === "PROGRAMADO") return "programado";
  if (s === "EDITADO") return "editado";
  if (s === "NEGADO") return "negado";
  if (s === "REPROGRAMAR" || s === "REPROGRAMADO") return "reprogramado";
  if (s === "APROVADO") return "aprovado";
  if (s === "CANCELADO") return "cancelado";

  return String(raw).toLowerCase().trim() || "nao_iniciado";
}

function getPreMetaEpochFromRow(obj) {
  const explicitEpoch = Number(obj?.pre_meta_expira_em_epoch ?? NaN);
  if (Number.isFinite(explicitEpoch) && explicitEpoch > 0) return explicitEpoch;

  if (statusCanonW2(obj) !== "pre_programacao") return null;

  const dataKey = toKeyYYYYMMDD(
    obj?.data ?? obj?.data_programacao ?? obj?.data_programada ?? null
  );
  if (!dataKey) return null;

  const expiry = dayjs
    .tz(`${dataKey} ${W2_PRE_META_HORA}`, "YYYY-MM-DD HH:mm:ss", TZ)
    .subtract(1, "day");

  return expiry.isValid() ? expiry.valueOf() : null;
}

/* =================== ✅ Ordenação por status dentro do DIA =================== */

const STATUS_WEIGHT_W2 = {
  "": 0,
  em_andamento: 0,
  validacao: 0,

  editado: 1,
  primeira_previa: 1,
  segunda_previa: 1,

  reprogramar: 2,

  programado: 3,

  finalizado: 4,
  executado: 4,

  negado: 5,
};

const STATUS_WEIGHT_W2_PROPRIO = {
  "": 0,
  nao_iniciado: 0,
  pre_programacao: 1,
  editado: 2,
  reprogramado: 3,
  programado: 4,
  aprovado: 5,
  cancelado: 6,
  negado: 7,
};

function statusW2ForSort(p) {
  const raw = p?.status_wf2 ?? p?.etapa_atual ?? p?.status ?? p?.fase ?? p?.etapa ?? "";
  const s = raw ? String(raw).toLowerCase().trim() : "";

  if (s === "em_validacao_automatica") return "validacao";
  if (s === "programado") return "programado";
  if (s === "editado") return "editado";
  if (s === "negado") return "negado";
  if (s === "reprogramar") return "reprogramar";
  if (s === "finalizado") return "finalizado";

  if (s === "executado") return "executado";
  if (s === "primeira_previa" || s === "segunda_previa") return s;
  if (s === "em_andamento") return "em_andamento";

  return s || "";
}

/* =================== Extratores RT (tolerante) =================== */
function extractW2FromMsg(msg) {
  if (!msg || typeof msg !== "object") return null;

  if (msg?.programado?.data && typeof msg.programado.data === "object") return msg.programado.data;
  if (msg?.w2 && typeof msg.w2 === "object") return msg.w2;
  if (msg?.row && typeof msg.row === "object") return msg.row;
  if (msg?.data && typeof msg.data === "object" && (msg.data.codigo_unico || msg.data.id)) return msg.data;

  const p = msg?.payload;
  if (p && typeof p === "object") {
    if (p?.programado?.data && typeof p.programado.data === "object") return p.programado.data;
    const d = p?.data ?? p;

    if (d?.programado?.data && typeof d.programado.data === "object") return d.programado.data;
    if (d?.w2 && typeof d.w2 === "object") return d.w2;
    if (d?.row && typeof d.row === "object") return d.row;
    if (d?.data && typeof d.data === "object" && (d.data.codigo_unico || d.data.id)) return d.data;
    if (d && typeof d === "object" && (d.codigo_unico || d.id)) return d;
  }

  if (msg?.codigo_unico || msg?.id) return msg;
  return null;
}

function extractCodigoFromMsg(msg) {
  if (!msg || typeof msg !== "object") return null;
  return (
    msg?.codigo_unico ||
    msg?.codigo ||
    msg?.id ||
    msg?.w2?.codigo_unico ||
    msg?.w2?.id ||
    msg?.row?.codigo_unico ||
    msg?.row?.id ||
    msg?.payload?.codigo_unico ||
    msg?.payload?.id ||
    msg?.payload?.w2?.codigo_unico ||
    msg?.payload?.w2?.id ||
    null
  );
}

function extractKindFromMsg(msg) {
  return msg?.kind || msg?.event || msg?.action || msg?.type || "";
}

/* =================== Página =================== */
export default function W2Page({ onBack = () => {} }) {
  // range padrão: D-1 até D+28
  const hojeBase = dayjs.tz().startOf("day");
  const defaultStart = hojeBase.subtract(1, "day");

  const [dataInicial, setDataInicial] = useState(defaultStart);
  const [dataFinal, setDataFinal] = useState(defaultStart.add(29, "day"));

  const [filtroGerencia, setFiltroGerencia] = useState([]);
  const [filtroCoordenador, setFiltroCoordenador] = useState([]);
  const [filtroAtividade, setFiltroAtividade] = useState([]);
  const [filtroAtivo, setFiltroAtivo] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState([]);

  const filtroGerenciaDeferred = useDeferredValue(filtroGerencia);
  const filtroCoordenadorDeferred = useDeferredValue(filtroCoordenador);
  const filtroAtividadeDeferred = useDeferredValue(filtroAtividade);
  const filtroAtivoDeferred = useDeferredValue(filtroAtivo);
  const filtroStatusDeferred = useDeferredValue(filtroStatus);

  const [rows, setRows] = useState([]);
  const [dragInfo, setDragInfo] = useState(null);

  const [fichaOpen, setFichaOpen] = useState(false);
  const [fichaData, setFichaData] = useState(null);

  const [novaOpen, setNovaOpen] = useState(false);

  const [infoOpen, setInfoOpen] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoError, setInfoError] = useState("");
  const [infoRows, setInfoRows] = useState([]);
  const [dateError, setDateError] = useState("");

  const calRef = useRef(null);
  const sidebarRef = useRef(null);
  const summaryRef = useRef(null);
  const userScrolledRef = useRef(false);
  const autoScrollingRef = useRef(false);
  const didInitialAutoScrollRef = useRef(false);
  const defaultEnd = useMemo(() => defaultStart.add(29, "day"), [defaultStart]);

  /* ===== normalize + upsert/remove ===== */
  const normalizeRowForState = useCallback((p) => {
    if (!p || typeof p !== "object") return null;
    const dKey = toKeyYYYYMMDD(p.data);
    const preMetaEpoch = getPreMetaEpochFromRow(p);
    return {
      ...p,
      data: dKey || p.data,
      gerencia: normalizeFilterValue(p.gerencia),
      coordenador: normalizeFilterValue(p.coordenador),
      atividade: normalizeFilterValue(p.atividade),
      ativo: normalizeFilterValue(p.ativo),
      etapa_atual: statusCanonW2(p),
      ...(Number.isFinite(preMetaEpoch) ? { pre_meta_expira_em_epoch: preMetaEpoch } : {}),
    };
  }, []);

  const upsertRow = useCallback(
    (incoming) => {
      const r0 = normalizeRowForState(incoming);
      if (!r0) return;

      const cu = r0.codigo_unico != null ? String(r0.codigo_unico) : "";
      const id = r0.id != null ? String(r0.id) : "";

      setRows((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        const idx = arr.findIndex((x) => {
          const xcu = x?.codigo_unico != null ? String(x.codigo_unico) : "";
          const xid = x?.id != null ? String(x.id) : "";
          if (cu && xcu) return xcu === cu;
          if (!cu && id && xid) return xid === id;
          if (cu && !xcu && xid && id) return xid === id;
          if (!cu && xcu && id && xid) return xid === id;
          return false;
        });

        if (idx >= 0) {
          const next = arr.slice();
          next[idx] = { ...(arr[idx] || {}), ...r0 };
          return next;
        }
        return [r0, ...arr];
      });
    },
    [normalizeRowForState]
  );

  const removeRowByCodigo = useCallback((codigoOrId) => {
    if (!codigoOrId) return;
    const key = String(codigoOrId);
    setRows((prev) =>
      (Array.isArray(prev) ? prev : []).filter((x) => {
        const xcu = x?.codigo_unico != null ? String(x.codigo_unico) : "";
        const xid = x?.id != null ? String(x.id) : "";
        return xcu !== key && xid !== key;
      })
    );
  }, []);

  /* ===== carregar do backend ===== */
  const carregar = useCallback(async () => {
    try {
      const resp = await listW2({
        limit: 5000,
        offset: 0,
        data_inicio: dataInicial.format("YYYY-MM-DD"),
        data_fim: dataFinal.format("YYYY-MM-DD"),
      });
      const arr = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : [];
      const normed = arr.map((p) => normalizeRowForState(p)).filter(Boolean);
      setRows(normed);
    } catch (e) {
      console.error("[W2] listar:", e?.message || e);
      setRows([]);
    }
  }, [dataFinal, dataInicial, normalizeRowForState]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /* ===== dias (depende do range) ===== */
  const dias = useMemo(() => getDiasCalendario(dataInicial, dataFinal), [dataInicial, dataFinal]);
  const diasKey = useMemo(() => dias.map((d) => d.format("YYYY-MM-DD")).join("|"), [dias]);

  /* ===== detectar scroll manual (não resetar) ===== */
  useEffect(() => {
    const el = calRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      if (autoScrollingRef.current) return;
      if (el.scrollLeft > 2) userScrolledRef.current = true;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [diasKey]);

  /* ===== rooms realtime ===== */
  const joinedRoomsRef = useRef(new Set());

  useEffect(() => {
    connectRealtime().catch(() => {});

    const baseRooms = new Set([makeWorkflowAllRoom("wf2")].filter(Boolean));
    for (const d of dias) {
      const room = makeWorkflowDayRoom("wf2", d.format("YYYY-MM-DD"));
      if (room) baseRooms.add(room);
    }

    const prev = joinedRoomsRef.current;
    for (const r of prev) if (!baseRooms.has(r)) leaveRoom(r);
    for (const r of baseRooms) if (!prev.has(r)) joinRoom(r);

    joinedRoomsRef.current = baseRooms;

    return () => {
      for (const r of joinedRoomsRef.current) leaveRoom(r);
      joinedRoomsRef.current = new Set();
    };
  }, [diasKey, dias]);

  /* ===== RT handler único ===== */
  const rtReloadTimerRef = useRef(null);
  const scheduleReload = useCallback(() => {
    if (rtReloadTimerRef.current) clearTimeout(rtReloadTimerRef.current);
    rtReloadTimerRef.current = setTimeout(() => {
      carregar();
    }, 200);
  }, [carregar]);

  useEffect(() => {
    connectRealtime().catch(() => {});

    const handler = (msg) => {
      const w2 = extractW2FromMsg(msg);
      if (w2 && typeof w2 === "object") {
        upsertRow(w2);
        return;
      }

      const kind = String(extractKindFromMsg(msg) || "").toLowerCase();
      const cu = extractCodigoFromMsg(msg);

      const isDeleted =
        kind.includes("delete") ||
        kind.includes("deleted") ||
        kind.includes("remov") ||
        String(msg?.kind || "") === RT.DELETED;

      if (isDeleted && cu) {
        removeRowByCodigo(cu);
        return;
      }

      const isMoved = String(msg?.kind || "") === RT.MOVED || kind.includes("moved") || kind.includes("move");
      if (isMoved && cu) {
        const rawNewDate =
          msg?.dataNova ??
          msg?.data ??
          msg?.payload?.dataNova ??
          msg?.payload?.data ??
          msg?.payload?.payload?.dataNova ??
          msg?.payload?.payload?.data ??
          null;

        const key = toKeyYYYYMMDD(rawNewDate);
        if (key) {
          setRows((prev) =>
            (Array.isArray(prev) ? prev : []).map((x) => {
              const xcu = x?.codigo_unico != null ? String(x.codigo_unico) : "";
              const xid = x?.id != null ? String(x.id) : "";
              if (xcu === String(cu) || xid === String(cu)) return { ...x, data: key };
              return x;
            })
          );
        }
        scheduleReload();
        return;
      }

      scheduleReload();
    };

    rtOn(RT.WF2_CHANGED, handler);
    rtOn(RT.CREATED, handler);
    rtOn(RT.UPDATED, handler);
    rtOn(RT.MOVED, handler);
    rtOn(RT.DELETED, handler);

    const onLocal = (ev) => handler(ev?.detail);
    window.addEventListener(W2_LOCAL_EVENT, onLocal);

    return () => {
      rtOff(RT.WF2_CHANGED, handler);
      rtOff(RT.CREATED, handler);
      rtOff(RT.UPDATED, handler);
      rtOff(RT.MOVED, handler);
      rtOff(RT.DELETED, handler);

      window.removeEventListener(W2_LOCAL_EVENT, onLocal);

      if (rtReloadTimerRef.current) clearTimeout(rtReloadTimerRef.current);
      rtReloadTimerRef.current = null;
    };
  }, [upsertRow, removeRowByCodigo, scheduleReload]);

  useEffect(() => {
    const onReconnect = () => scheduleReload();
    onRealtimeMeta(RT_META.RECONNECTED, onReconnect);
    return () => offRealtimeMeta(RT_META.RECONNECTED, onReconnect);
  }, [scheduleReload]);

  /* ===== sidebar scaling ===== */
  useLayoutEffect(() => {
    const apply = () => {
      const el = sidebarRef.current;
      if (!el) return;
      const h = el.clientHeight || window.innerHeight;
      const BASE = 1080;
      const s = h / BASE;

      const LIFT = 26 * s;
      const map = {
        "--y-menu": 46 * s,
        "--y-title-data": (216 - 26) * s - LIFT,
        "--y-data": 216 * s - LIFT,
        "--y-title-ger": (304 - 26) * s - LIFT,
        "--y-ger": 304 * s - LIFT,
        "--y-title-coord": (394 - 26) * s - LIFT,
        "--y-coord": 394 * s - LIFT,
        "--y-title-ati": (484 - 26) * s - LIFT,
        "--y-ati": 484 * s - LIFT,
        "--y-title-ativos": (574 - 26) * s - LIFT,
        "--y-ativos": 574 * s - LIFT,
        "--y-title-status": (664 - 26) * s - LIFT,
        "--y-status": 664 * s - LIFT,
      };

      const CTRL_H = 42, GAP = 12, SUMMARY_GAP = 18, BOTTOM = 22;
      const yNova = h - BOTTOM - CTRL_H - LIFT;
      const yInfo = yNova - GAP - CTRL_H;
      const SUMMARY_H = Math.max(116, Math.ceil(summaryRef.current?.offsetHeight || 0));
      const yLimpar = yInfo - GAP - CTRL_H;
      const yResumo = yLimpar - SUMMARY_GAP - SUMMARY_H;

      Object.entries(map).forEach(([k, v]) => el.style.setProperty(k, `${Math.round(v)}px`));
      el.style.setProperty("--y-resumo", `${Math.round(yResumo)}px`);
      el.style.setProperty("--y-limpar", `${Math.round(yLimpar)}px`);
      el.style.setProperty("--y-nova", `${Math.round(yNova)}px`);
      el.style.setProperty("--y-info", `${Math.round(yInfo)}px`);
    };

    apply();
    window.addEventListener("resize", apply);
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => apply())
        : null;
    if (ro) {
      if (sidebarRef.current) ro.observe(sidebarRef.current);
      if (summaryRef.current) ro.observe(summaryRef.current);
    }
    return () => {
      window.removeEventListener("resize", apply);
      ro?.disconnect();
    };
  }, []);

  /* ===== coleções derivadas ===== */
  const visiveis = useMemo(() => {
    const ini = dataInicial.startOf("day");
    const fim = dataFinal.endOf("day");

    return (Array.isArray(rows) ? rows : []).filter((p) => {
      const d = toLocalDay(p?.data);
      return d.isValid() && d.isBetween(ini, fim, "day", "[]");
    });
  }, [rows, dataInicial, dataFinal]);

  const gerencias = useMemo(
    () => uniqFilterOptions(visiveis.map((p) => p.gerencia)),
    [visiveis]
  );
  const coordenadores = useMemo(
    () => uniqFilterOptions(visiveis.map((p) => p.coordenador)),
    [visiveis]
  );
  const atividades = useMemo(
    () => uniqFilterOptions(visiveis.map((p) => p.atividade)),
    [visiveis]
  );
  const ativos = useMemo(
    () => uniqFilterOptions(visiveis.map((p) => p.ativo)),
    [visiveis]
  );

  const filtradas = useMemo(() => {
    const g = new Set((filtroGerencia || []).map(filterMatchKey).filter(Boolean));
    const c = new Set((filtroCoordenador || []).map(filterMatchKey).filter(Boolean));
    const a = new Set((filtroAtividade || []).map(filterMatchKey).filter(Boolean));
    const i = new Set((filtroAtivo || []).map(filterMatchKey).filter(Boolean));
    const s = new Set(filtroStatus || []);

    return visiveis.filter((p) => {
      const st = statusCanonW2(p);
      if (g.size && !g.has(filterMatchKey(p.gerencia))) return false;
      if (c.size && !c.has(filterMatchKey(p.coordenador))) return false;
      if (a.size && !a.has(filterMatchKey(p.atividade))) return false;
      if (i.size && !i.has(filterMatchKey(p.ativo))) return false;
      if (s.size && !s.has(st)) return false;
      return true;
    });
  }, [
    visiveis,
    filtroGerencia,
    filtroCoordenador,
    filtroAtividade,
    filtroAtivo,
    filtroStatus,
  ]);

  const isDefaultDateRange = useMemo(
    () =>
      dataInicial.isSame(defaultStart, "day") &&
      dataFinal.isSame(defaultEnd, "day"),
    [dataFinal, dataInicial, defaultEnd, defaultStart]
  );

  const resumoPeriodo = useMemo(() => {
    if (!isDefaultDateRange) {
      return {
        start: dataInicial.startOf("day"),
        end: dataFinal.startOf("day"),
      };
    }
    return getWeekRangeSundayToSaturday(dayjs.tz().startOf("day"));
  }, [dataFinal, dataInicial, isDefaultDateRange]);

  const resumoBase = useMemo(() => {
    const ini = resumoPeriodo.start.startOf("day");
    const fim = resumoPeriodo.end.endOf("day");
    const g = new Set((filtroGerencia || []).map(filterMatchKey).filter(Boolean));
    const c = new Set((filtroCoordenador || []).map(filterMatchKey).filter(Boolean));
    const a = new Set((filtroAtividade || []).map(filterMatchKey).filter(Boolean));
    const i = new Set((filtroAtivo || []).map(filterMatchKey).filter(Boolean));

    return (Array.isArray(rows) ? rows : []).filter((p) => {
      const d = toLocalDay(p?.data);
      if (!d.isValid() || !d.isBetween(ini, fim, "day", "[]")) return false;
      if (g.size && !g.has(filterMatchKey(p.gerencia))) return false;
      if (c.size && !c.has(filterMatchKey(p.coordenador))) return false;
      if (a.size && !a.has(filterMatchKey(p.atividade))) return false;
      if (i.size && !i.has(filterMatchKey(p.ativo))) return false;
      return true;
    });
  }, [
    filtroAtividade,
    filtroAtivo,
    filtroCoordenador,
    filtroGerencia,
    resumoPeriodo.end,
    resumoPeriodo.start,
    rows,
  ]);

  const resumoSidebar = useMemo(() => {
    let programado = 0;
    let negado = 0;
    let reprogramado = 0;
    for (const row of resumoBase) {
      const st = statusCanonW2(row);
      if (st === "programado") programado += 1;
      if (st === "negado") negado += 1;
      if (st === "reprogramado") reprogramado += 1;
    }
    return {
      total: resumoBase.length,
      programado,
      negado,
      reprogramado,
      periodo: `${resumoPeriodo.start.format("DD/MM")} a ${resumoPeriodo.end.format("DD/MM")}`,
    };
  }, [resumoBase, resumoPeriodo.end, resumoPeriodo.start]);

  const porDia = useMemo(() => {
    const base = dias.reduce((acc, d) => {
      acc[d.format("YYYY-MM-DD")] = [];
      return acc;
    }, {});

    for (const p of filtradas) {
      const key = toKeyYYYYMMDD(p.data);
      if (key && base[key]) base[key].push(p);
    }

    Object.keys(base).forEach((k) => {
      base[k].sort((a, b) => {
        const sa = statusCanonW2(a);
        const sb = statusCanonW2(b);

        const wa = STATUS_WEIGHT_W2_PROPRIO[sa] ?? 99;
        const wb = STATUS_WEIGHT_W2_PROPRIO[sb] ?? 99;
        if (wa !== wb) return wa - wb;

        const ma = extrairMinutosDoProg(a);
        const mb = extrairMinutosDoProg(b);
        if (ma !== mb) return ma - mb;

        const ida = String(a.codigo_unico || a.id || "");
        const idb = String(b.codigo_unico || b.id || "");
        return ida.localeCompare(idb);
      });
    });

    return base;
  }, [dias, filtradas]);

  /* ===== Auto-scroll ===== */
  useEffect(() => {
    if (!rows.length || !visiveis.length) return;
    if (didInitialAutoScrollRef.current) return;
    if (userScrolledRef.current) {
      didInitialAutoScrollRef.current = true;
      return;
    }

    const firstKey = toKeyYYYYMMDD(
      visiveis
        .slice()
        .sort((a, b) => toLocalDay(a.data).valueOf() - toLocalDay(b.data).valueOf())[0].data
    );

    const idx = dias.findIndex((d) => d.format("YYYY-MM-DD") === firstKey);
    if (idx >= 0 && calRef.current) {
      const COL_W = 202, GAP = 8;
      const left = Math.max(0, idx * (COL_W + GAP) - 12);
      autoScrollingRef.current = true;
      calRef.current.scrollTo({ left, behavior: "auto" });
      requestAnimationFrame(() => {
        autoScrollingRef.current = false;
      });
    }
    didInitialAutoScrollRef.current = true;
  }, [rows, visiveis, dias]);

  /* ===== DnD ===== */
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const srcIdx = Number(result.source.droppableId);
    const dstIdx = Number(result.destination.droppableId);
    if (Number.isNaN(srcIdx) || Number.isNaN(dstIdx)) return;
    if (srcIdx === dstIdx) return;

    const srcKey = dias[srcIdx].format("YYYY-MM-DD");
    const prog = porDia[srcKey]?.[result.source.index];
    if (!prog) return;

    const dataNova = dias[dstIdx].format("YYYY-MM-DD");
    const atualKey = toKeyYYYYMMDD(prog.data);
    if (atualKey !== dataNova) setDragInfo({ prog, dataNova });
  };

  async function confirmarMover(justificativaMover) {
    if (!dragInfo) return;
    const { prog, dataNova } = dragInfo;

    // bloqueia mover para data passada antes de chamar backend
    const hoje = dayjs.tz().startOf("day");
    const alvo = dayjs.tz(dataNova, "YYYY-MM-DD", TZ);
    if (!alvo.isValid() || alvo.isBefore(hoje)) {
      setDateError("Data não pode ser no passado");
      setDragInfo(null);
      return;
    }

    const snapshot = JSON.parse(JSON.stringify(rows));
    setRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((p) =>
        p.codigo_unico === prog.codigo_unico ? { ...p, data: dataNova } : p
      )
    );

    try {
      const causador = justificativaMover?.causador || null;
      const causa_primaria = justificativaMover?.causa_primaria || null;
      const motivo =
        causador != null && causador !== ""
          ? `Causador: ${causador} | Causa primária: ${causa_primaria || "-"}`
          : "Alteração de data via drag-and-drop";

      const moved = await moveDateW2({
        codigo_unico: prog.codigo_unico,
        data: dataNova,
        causador,
        causa_primaria,
        motivo: motivo || "Alteração de data via drag-and-drop",
      });
      if (!moved) {
        await advanceStageW2({
          codigo_unico: prog.codigo_unico,
          etapa_nova: prog.etapa_atual || "wf_2",
        });
      }
      setDragInfo(null);
      await carregar();
    } catch (e) {
      console.error("[W2] mover-data:", e?.message || e);
      setRows(snapshot);
      setDragInfo(null);
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Data não pode ser no passado";
      if (/passad/i.test(msg)) {
        setDateError("Data não pode ser no passado");
      } else {
        alert("Erro ao mover card.");
      }
    }
  }

  const limpar = () => {
    setFiltroGerencia([]);
    setFiltroCoordenador([]);
    setFiltroAtividade([]);
    setFiltroAtivo([]);
    setFiltroStatus([]);

    const hoje = dayjs.tz().startOf("day");
    const start = hoje.subtract(1, "day");
    setDataInicial(start);
    setDataFinal(start.add(29, "day"));
  };

  const handleCloseFicha = () => {
    setFichaOpen(false);
  };

  const handleFinalizeFicha = (d) => {
    console.log("finalizar", d);
    setFichaOpen(false);
  };

  const fetchInfo = useCallback(async () => {
    setInfoLoading(true);
    setInfoError("");
    try {
      // Busca os 2 próximos dias (D+1 e D+2)
      const tomorrow = dayjs.tz().add(1, "day").format("YYYY-MM-DD");
      const thirdDay = dayjs.tz().add(3, "day").format("YYYY-MM-DD");
      const rows = await listW2({ limit: 100, offset: 0, data_inicio: tomorrow, data_fim: thirdDay });
      setInfoRows(Array.isArray(rows) ? rows : rows?.rows || []);
    } catch (e) {
      setInfoError(e?.message || "Falha ao carregar WF-2");
    } finally {
      setInfoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (infoOpen) {
      fetchInfo();
    }
  }, [infoOpen, fetchInfo]);

  return (
    <div className="w2-root">
      <aside className="w2-sidebar" ref={sidebarRef}>
        <button type="button" className="w2-btn w2-abs w2-posMenu" onClick={onBack}>
          Menu Principal
        </button>

        <div className="w2-titleSmall w2-abs w2-posTitleData">Filtrar Data:</div>
        <div className="w2-rowDates w2-abs w2-posData">
          <W2DateDropdown
            value={dataInicial}
            maxDate={dataFinal}
            ariaLabel="Selecionar data inicial"
            panelAlign="left"
            onChange={(d0) => {
              setDataInicial(d0);
              if (d0.isAfter(dataFinal)) setDataFinal(d0);
            }}
          />
          <W2DateDropdown
            value={dataFinal}
            minDate={dataInicial}
            ariaLabel="Selecionar data final"
            panelAlign="right"
            onChange={(d0) => {
              setDataFinal(d0);
              if (d0.isBefore(dataInicial)) setDataInicial(d0);
            }}
          />
        </div>

        <div className="w2-titleSmall w2-abs w2-posTitleGer">Filtrar Gerências:</div>
        <div className="w2-abs w2-posGer">
          <MultiSelect
            options={gerencias}
            value={filtroGerencia}
            onChange={setFiltroGerencia}
            placeholder="Todas as gerências"
            title="Gerências"
            searchable
            searchPlaceholder="Buscar gerência..."
          />
        </div>

        <div className="w2-titleSmall w2-abs w2-posTitleCoord">Filtrar Coordenadores:</div>
        <div className="w2-abs w2-posCoord">
          <MultiSelect
            options={coordenadores}
            value={filtroCoordenador}
            onChange={setFiltroCoordenador}
            placeholder="Todos os coordenadores"
            title="Coordenadores"
            searchable
            searchPlaceholder="Buscar coordenador..."
          />
        </div>

        <div className="w2-titleSmall w2-abs w2-posTitleAti">Filtrar Atividades:</div>
        <div className="w2-abs w2-posAti">
          <MultiSelect
            options={atividades}
            value={filtroAtividade}
            onChange={setFiltroAtividade}
            placeholder="Todas as atividades"
            title="Atividades"
            searchable
            searchPlaceholder="Buscar atividade..."
          />
        </div>

        <div className="w2-titleSmall w2-abs w2-posTitleAtivos">Filtrar Ativos:</div>
        <div className="w2-abs w2-posAtivos">
          <MultiSelect
            options={ativos}
            value={filtroAtivo}
            onChange={setFiltroAtivo}
            placeholder="Todos os ativos"
            title="Ativos"
            searchable
            searchPlaceholder="Buscar ativo..."
          />
        </div>

        <div className="w2-titleSmall w2-abs w2-posTitleStatus">Filtrar Status:</div>
        <div className="w2-abs w2-posStatus">
          <MultiSelect
            options={STATUS_FILTER_OPTIONS_W2}
            value={filtroStatus}
            onChange={setFiltroStatus}
            placeholder="Todos os status"
            title="Status"
          />
        </div>

        <div className="w2-abs w2-posResumo">
          <div className="w2-summary" ref={summaryRef}>
            <div className="w2-summaryHead">
              <div className="w2-summaryTitle">Resumo do período</div>
              <div className="w2-summaryRange">{resumoSidebar.periodo}</div>
            </div>

            <div className="w2-summaryGrid">
              <div className="w2-summaryCard">
                <span className="w2-summaryLabel">Programado</span>
                <strong className="w2-summaryValue">{resumoSidebar.programado}</strong>
              </div>
              <div className="w2-summaryCard">
                <span className="w2-summaryLabel">Negado</span>
                <strong className="w2-summaryValue">{resumoSidebar.negado}</strong>
              </div>
              <div className="w2-summaryCard">
                <span className="w2-summaryLabel">Reprogramado</span>
                <strong className="w2-summaryValue">{resumoSidebar.reprogramado}</strong>
              </div>
              <div className="w2-summaryCard w2-summaryCardTotal">
                <span className="w2-summaryLabel">Total</span>
                <strong className="w2-summaryValue">{resumoSidebar.total}</strong>
              </div>
            </div>
          </div>
        </div>

        <button className="w2-btn w2-abs w2-posLimpar" onClick={limpar} title="Limpar filtros">
          Limpar filtros
        </button>

        <button className="w2-btn w2-abs w2-posNova" onClick={() => setNovaOpen(true)}>
          Nova Programação
        </button>

        <button
          className="w2-btn w2-abs w2-posInfo"
          onClick={() => setInfoOpen(true)}
          title="Ver dados WF-2 (teste)"
        >
          Preview
        </button>
      </aside>

      <div className="w2-content">
        <div className="w2-contentHeader">
          <h2 className="w2-title">Programações </h2>
        </div>

        <div className="w2-calWrapper" ref={calRef}>
          <DragDropContext onDragEnd={onDragEnd}>
            {dias.map((dia, idx) => {
              const diaKey = dia.format("YYYY-MM-DD");
              const progs = porDia[diaKey] || [];
              const nomeDia = DIAS_PT[dia.day()];

              return (
                <div className="w2-colunaDia" key={diaKey}>
                  <div className="w2-colunaHeader">
                    <span className="w2-colunaHeaderDay">{nomeDia}</span>
                    <span className="w2-colunaHeaderDate">{dia.format("DD/MM/YYYY")}</span>
                  </div>

                  <Droppable droppableId={String(idx)} direction="vertical">
                    {(provided) => (
                      <div className="w2-cards" ref={provided.innerRef} {...provided.droppableProps}>
                        {progs.length === 0 && <div className="w2-vazio">—</div>}

                        {progs.map((p, i) => (
                          <Draggable
                            key={p.codigo_unico || p.id}
                            draggableId={String(p.codigo_unico || p.id)}
                            index={i}
                          >
                            {(prov, snapshot) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                style={prov.draggableProps.style}
                                className="w2-cardWrap"
                              >
                                <W2Card
                                  data={p}
                                  isDragging={snapshot.isDragging}
                                  onClick={() => {
                                    setFichaData(p);
                                    setFichaOpen(true);
                                  }}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </DragDropContext>
        </div>
      </div>

      {/* ✅ Modal separado (padrão estética) */}
      <W2MoverDataModal
        open={!!dragInfo}
        onClose={() => setDragInfo(null)}
        onSubmit={confirmarMover}
      />

      <W2FichaModal
        open={!!fichaOpen}
        data={fichaData || {}}
        onClose={handleCloseFicha}
        onEdit={(d) => console.log("editar", d)}
        onFinalize={handleFinalizeFicha}
      />

      <W2NovaProgramar
        open={novaOpen}
        defaultDate={dataInicial.format("YYYY-MM-DD")}
        onClose={() => setNovaOpen(false)}
        onCreated={(row) => {
          setNovaOpen(false);
          if (row && typeof row === "object") upsertRow(row);
          carregar();
        }}
      />

      <W2InfoPopup
        open={infoOpen}
        loading={infoLoading}
        error={infoError}
        data={infoRows}
        onClose={() => setInfoOpen(false)}
        onRefresh={fetchInfo}
      />

      <W2DateErrorModal
        open={!!dateError}
        message={dateError || "Data não pode ser no passado"}
        onClose={() => setDateError("")}
      />
    </div>
  );
}


